import { useEffect, useMemo, useState } from "react";
import styles from "./Blog.module.scss";
import { Link, useParams } from "react-router-dom";
import { CLOUDINARY_BASE_URL } from "../config/cloudinary";
import Breadcrumb from "../Breadcrumb";
import { useLanguage } from "../contexts/LanguageContext";
import { trackCtaClick, trackEvent } from "../utils/analytics";
import { getBlogCategoryLabel } from "../utils/blogCategoryLabel";
import {
  getLocalizedValue,
  localizeArticleList,
  localizeCategoryItems,
  localizeTagItems,
} from "../utils/localizedContent";
import { blogText } from "../i18n/blog";
import BlogMemoryCta from "../components/BlogMemoryCta/BlogMemoryCta";

const img = (path) => `${CLOUDINARY_BASE_URL}${path}`;

const ARTICLE_BASE_PATH = "/blog";
const HIDDEN_BLOG_SLUGS = new Set([
  "jet-secret-beach-tour",
  "jet-private-secret-tour-experience",
]);
const HIDDEN_BLOG_TAG_KEYS = new Set(["ジェット", "ジェットツアー"]);

export const isVisibleBlogArticle = (article) => {
  return !HIDDEN_BLOG_SLUGS.has(article?.slug);
};

export const isVisibleBlogTag = (tag) => {
  const key = String(tag?.key || "").replace("#", "");
  return !HIDDEN_BLOG_TAG_KEYS.has(key);
};

const uniqueArticlesBySlug = (articleList) => {
  const articleMap = new Map();

  articleList.forEach((article) => {
    const key = article.slug || article.id;

    if (!articleMap.has(key)) {
      articleMap.set(key, article);
    }
  });

  return Array.from(articleMap.values());
};

const heroImage = img("v1783928437/DJI_20260707115907_0008_D_xag4ot.webp");
const cafeImage = img(
  "v1783352339/AD_4nXf2yA3puqm63h-XEHfk_ltPQ-m0Sy4IQCU-m0yCaojShxZt7-CDQQ1B8UUjJ5iQC-szsuwzS9Vq4ScPOBElN52vu_oxEkcKu2UbdK-gsQe_WVuQKT9E9iGjaVqKC7vIMduz6M-_ylvli6.png",
);
const lunchImage = img("v1784292789/DSC00325_htmozu.jpg");
const dinnerImage = img("v1784292789/DSC00325_htmozu.jpg");
const nightDinnerGuideImage = img("v1789053600/IMG_6917-edited_u83koj.jpg");
const gelatoImage = img(
  "v1783352339/AD_4nXf2yA3puqm63h-XEHfk_ltPQ-m0Sy4IQCU-m0yCaojShxZt7-CDQQ1B8UUjJ5iQC-szsuwzS9Vq4ScPOBElN52vu_oxEkcKu2UbdK-gsQe_WVuQKT9E9iGjaVqKC7vIMduz6M-_ylvli6.png",
);
const sobaImage = img("v1784689797/soba_yyrxly.jpg");
const sataAndagiImage = img("v1784690818/23_mbegc4.png");
const bridgeImage = img("v1784682749/DJI_20260721090315_0012_D_ndyr5p.webp");
const hotelGuideImage = img("v1788839327/images_fzmtmq.jpg");
const beachImage = img("v1783928437/DJI_20260707115907_0008_D_xag4ot.webp");
const rainyImage = img("v1788261035/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-09-01_20.09.37_a4hlqg.png");
const starImage = img("v1785311449/DSC00588-2_x2m1d1.webp");

const articles = [
  {
    id: 1,
    slug: "cafe",
    category: "Cafe",
    title: "宮古島のおすすめ厳選カフェ",
    text: "海が見える絶景カフェから、緑に囲まれた癒しカフェ、雨の日でも楽しめるカフェまで紹介。",
    date: "2026.06.27",
    readTime: "8分",
    image: cafeImage,
    tags: ["#宮古島", "#カフェ巡り", "#映えスポット"],
  },
  {
    id: 7,
    slug: "bae-cafe-best5",
    category: "Cafe",
    title: "写真撮るならここ！映えるカフェBEST6",
    text: "宮古島で写真や動画に残したい、海・緑・南国感がきれいに映えるカフェを厳選。",
    date: "2026.07.22",
    readTime: "6分",
    image: cafeImage,
    tags: ["#宮古島", "#カフェ巡り", "#映えスポット", "#撮影"],
  },
  {
    id: 14,
    slug: "cafe-by-area",
    category: "Cafe",
    title: "場所別で探す宮古島のおすすめカフェ",
    text: "伊良部島、来間島、池間島、宮古島本島のエリア別に、ドライブ途中で立ち寄りやすいカフェを紹介。",
    date: "2026.07.25",
    readTime: "7分",
    image: cafeImage,
    tags: ["#宮古島", "#カフェ巡り", "#伊良部島", "#来間島", "#池間島"],
  },
  {
    id: 2,
    slug: "recommend-lunch",
    category: "Gourmet",
    title: "宮古島のおすすめランチ・グルメ",
    text: "カレー、島食材ランチ、カフェごはん、肉料理まで。宮古島旅行で立ち寄りたいおすすめランチを紹介。",
    date: "2026.06.28",
    readTime: "7分",
    image: lunchImage,
    tags: ["#宮古島", "#グルメ", "#ランチ"],
  },
  {
    id: 3,
    slug: "recommend-soba",
    category: "Gourmet",
    title: "宮古そばの名店5選",
    text: "市街地で行きやすいお店から、北部ドライブ、南部方面で立ち寄りたいお店まで紹介。",
    date: "2026.06.28",
    readTime: "7分",
    image: sobaImage,
    tags: ["#宮古島", "#グルメ", "#宮古そば", "#ランチ"],
  },
  {
    id: 4,
    slug: "recommend-sata-andagi",
    category: "Gourmet",
    title: "宮古島でサーターアンダギー食べるならここ5選",
    text: "なかゆくい商店、ななみカフェ、カフェ オルオキ、海美来、七福さたぱんびん。旅のおやつに立ち寄りたい5店を紹介。",
    date: "2026.06.28",
    readTime: "6分",
    image: sataAndagiImage,
    tags: ["#宮古島", "#グルメ", "#サーターアンダギー", "#お土産"],
  },
  {
    id: 21,
    slug: "night-dinner-top5",
    category: "Gourmet",
    title: "宮古島の夜ご飯TOP5",
    text: "島おでん、民謡居酒屋、ローカル居酒屋、宮古牛まで。宮古島旅行の夜に行きたいお店を厳選。",
    date: "2026.07.31",
    readTime: "6分",
    image: nightDinnerGuideImage,
    tags: ["#宮古島", "#グルメ", "#夜ご飯", "#夜遊び"],
  },
  {
    id: 23,
    slug: "miyakojima-yakiniku-top3",
    category: "Gourmet",
    title: "宮古島焼肉店TOP3",
    text: "宮古牛を楽しめる焼肉店を、旅行中に選びやすい価格帯の目安つきで紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: dinnerImage,
    tags: ["#宮古島", "#グルメ", "#焼肉", "#宮古牛"],
  },
  {
    id: 24,
    slug: "miyakojima-sweets-best14",
    category: "Gourmet",
    title: "宮古島スイーツ特集 14選",
    text: "スムージー、マンゴー、さたぱんびん、アイス、ドーナツまで。宮古島旅行で立ち寄りたいスイーツ店を紹介。",
    date: "2026.07.31",
    readTime: "8分",
    image: sataAndagiImage,
    tags: ["#宮古島", "#グルメ", "#スイーツ", "#宮古島スイーツ"],
  },
  {
    id: 22,
    slug: "miyakojima-gelato-best5",
    category: "Gourmet",
    title: "宮古島でジェラート食べるならここ5選",
    text: "リッコジェラート、Ninufa、ブルーシールなど、宮古島旅行中に立ち寄りたい冷たいスイーツを紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: gelatoImage,
    tags: ["#宮古島", "#グルメ", "#ジェラート", "#スイーツ"],
  },
  {
    id: 5,
    slug: "drive-scenic-spots",
    category: "Drive",
    title: "宮古島で車で行ける絶景スポット9選",
    text: "ドライブで行ける絶景から、サンセットが綺麗に見える場所まで。宮古島旅行で立ち寄りたいスポットを紹介。",
    date: "2026.06.20",
    readTime: "7分",
    image: img("v1782544346/DJI_20260619092553_0013_D_mggybx.jpg"),
    tags: ["#宮古島", "#ドライブ", "#サンセット", "#映えスポット", "#ドローン"],
  },
  {
    id: 25,
    slug: "miyakojima-superb-view-spots",
    category: "Shooting",
    title: "宮古島旅行で絶対に行きたい絶景スポット",
    text: "王道の絶景から秘境スポット、究極の非日常体験を残せる秘境フォトツアーまで紹介。",
    date: "2026.07.31",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#絶景スポット", "#撮影", "#ドローン"],
  },
  {
    id: 26,
    slug: "miyakojima-cheap-local-rentacar",
    category: "Drive",
    title: "宮古島 激安ローカルレンタカーまとめ",
    text: "宮古島で安く借りたい人向けに、ローカル系レンタカーと選ぶ時の注意点を紹介。",
    date: "2026.07.31",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#レンタカー", "#格安レンタカー", "#ドライブ"],
  },
  {
    id: 6,
    slug: "miyakojima-surfing",
    category: "Tour",
    title: "宮古島でサーフィンをするべき理由3選",
    text: "人が少ない海、宮古ブルーでの撮影、ローカルガイド付きの安心感。宮古島サーフィンの魅力を紹介。",
    date: "2026.07.19",
    readTime: "5分",
    image: img(
      "v1784354836/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-07-18_15.06.59_noluwe.png",
    ),
    tags: [
      "#宮古島",
      "#サーフィン",
      "#マリンアクティビティ",
      "#撮影",
      "#ドローン",
    ],
  },
  {
    id: 15,
    slug: "miyakojima-surfing-experience",
    category: "Tour",
    title: "宮古島サーフィン体験の様子",
    text: "朝の海、レッスン、波に乗る瞬間、写真・映像データのお渡しまで、実際の動画と写真で紹介。",
    date: "2026.07.27",
    readTime: "4分",
    image: img("v1785121770/IMG_5983_abqsgh.webp"),
    tags: [
      "#宮古島",
      "#サーフィン",
      "#マリンアクティビティ",
      "#撮影",
      "#ドローン",
      "#旅動画",
    ],
  },
  {
    id: 31,
    slug: "miyakojima-surfing-guide",
    category: "Tour",
    title: "宮古島来たらサーフィンしないと！",
    text: "サンライズ、初心者レッスン、多言語対応、ローカルガイドの安心感まで。宮古島サーフィン体験を動画で紹介。",
    date: "2026.09.01",
    readTime: "5分",
    image: img("v1788264796/IMG_6101_wqf7cj.jpg"),
    tags: [
      "#宮古島",
      "#宮古島サーフィン",
      "#サーフィン",
      "#マリンアクティビティ",
      "#ローカルガイド",
      "#多言語対応",
      "#撮影",
    ],
  },
  {
    id: 8,
    slug: "model-course-2n3d",
    category: "Drive",
    title: "宮古島2泊3日モデルプラン",
    text: "秘境フォトツアーや秘境SUP、八重干瀬、サンセット、夜ご飯まで楽しむ2泊3日プラン。",
    date: "2026.07.31",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#ドライブ", "#モデルコース", "#八重干瀬"],
  },
  {
    id: 38,
    slug: "girls-trip-model-course-2n3d",
    category: "Drive",
    title: "宮古島で映え女子旅！ 2泊3日のモデルコース",
    text: "写真・カフェ・ビーチ・サンセット・夜ご飯まで楽しむ女子旅モデルコース。",
    date: "2026.08.08",
    readTime: "6分",
    image: img("v1785776523/DSC00815_yry3re.webp"),
    tags: ["#宮古島", "#女子旅", "#モデルコース", "#映えスポット", "#撮影"],
  },
  {
    id: 39,
    slug: "miyakojima-girls-trip-hotels",
    category: "Spot",
    title: "宮古島で女子旅におすすめのホテル6選",
    text: "写真映え、プール、オーシャンビュー、アクセスで選ぶ女子旅向けホテル。",
    date: "2026.08.08",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#女子旅", "#ホテル", "#非日常体験"],
  },
  {
    id: 40,
    slug: "miyakojima-girls-trip-spots",
    category: "Spot",
    title: "宮古島周辺でおすすめの女子旅スポット",
    text: "ビーチ、橋、カフェ、サンセット、秘境SUPまで女子旅で行きたい場所を紹介。",
    date: "2026.08.08",
    readTime: "6分",
    image: img("v1784945639/DSC00440-2_syxyio.webp"),
    tags: ["#宮古島", "#女子旅", "#映えスポット", "#カフェ巡り", "#撮影"],
  },
  {
    id: 41,
    slug: "miyako-hotel-guide-by-area",
    category: "Hotel",
    title: "【エリア別】宮古島のおすすめホテル・リゾート",
    text: "伊良部島、シギラ・上野、平良・北部、来間島、与那覇前浜周辺までエリア別に紹介。",
    date: "2026.09.08",
    readTime: "10分",
    image: hotelGuideImage,
    tags: ["#宮古島", "#宮古島ホテル", "#リゾート", "#ヴィラ"],
  },
  {
    id: 42,
    slug: "miyakojima-night-dinner-guide",
    category: "Gourmet",
    title: "宮古島のおすすめ夜ご飯｜雰囲気別ガイド",
    text: "島おでん、民謡居酒屋、海鮮、宮古牛、しゃぶしゃぶまで。営業時間・定休日・価格帯つきで紹介。",
    date: "2026.09.10",
    readTime: "8分",
    image: dinnerImage,
    tags: ["#宮古島", "#宮古島グルメ", "#夜ご飯", "#居酒屋"],
  },
  {
    id: 24,
    slug: "model-course-3n4d",
    category: "Drive",
    title: "宮古島3泊4日モデルプラン",
    text: "秘境フォトツアー、秘境SUP、八重干瀬、星空撮影まで無理なく楽しむ3泊4日プラン。",
    date: "2026.07.31",
    readTime: "7分",
    image: bridgeImage,
    tags: ["#宮古島", "#ドライブ", "#モデルコース", "#星空撮影"],
  },
  {
    id: 33,
    slug: "miyakojima-starry-sky-spots",
    category: "Star",
    title: "宮古島で満天の星空を見るには？おすすめ星空スポット",
    text: "新月前後、月明かり、街灯の少ない場所など、満天の星空を見る条件とスポットを紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: starImage,
    tags: ["#宮古島", "#星空", "#星空スポット", "#星空撮影"],
  },
  {
    id: 34,
    slug: "miyakojima-today-travel-dashboard",
    category: "Drive",
    title: "宮古島を120%楽しむために！今日チェックしたい旅情報",
    text: "天気、日の出・日の入り、月の出・月入り、潮位、星空指数など出発前に見る情報を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: bridgeImage,
    tags: ["#宮古島", "#天気", "#潮位", "#星空"],
  },
  {
    id: 35,
    slug: "miyakojima-starry-season-2026",
    category: "Star",
    title:
      "宮古島の星空が綺麗に見える時期は？おすすめ日程・天の川情報【2026年版】",
    text: "新月前後、月明かり、天の川シーズン、2026年の星空フォトおすすめ日程を紹介。",
    date: "2026.07.31",
    readTime: "8分",
    image: starImage,
    tags: ["#宮古島", "#星空", "#天の川", "#新月"],
  },
  {
    id: 36,
    slug: "full-moon-party-japan-miyakojima",
    category: "Night",
    title: "日本に存在するフルムーンパーティとは？",
    text: "満月の夜に白をまとって集う、宮古島らしい非日常ナイトイベントを紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: starImage,
    tags: ["#宮古島", "#フルムーンパーティ", "#夜遊び", "#非日常体験"],
  },
  {
    id: 37,
    slug: "sunset-spots-best5",
    category: "Sunset",
    title: "宮古島のおすすめサンセットスポット5選",
    text: "17END、佐和田の浜、トゥリバーなど、夕日が綺麗に見える場所を紹介。",
    date: "2026.07.31",
    readTime: "6分",
    image: img("v1784466423/DJI_20260718160638_0015_D_gh8w0n.webp"),
    tags: ["#宮古島", "#サンセット", "#撮影", "#旅動画"],
  },
  {
    id: 9,
    slug: "without-car-plan",
    category: "Drive",
    title: "宮古島で車なしでも楽しむ1日プラン",
    text: "レンタカーなしでも楽しみたい方向けに、市街地・近場ビーチ・カフェを中心にした過ごし方を紹介。",
    date: "2026.07.22",
    readTime: "5分",
    image: rainyImage,
    tags: ["#宮古島", "#ドライブ", "#モデルコース", "#カフェ巡り"],
  },
  {
    id: 10,
    slug: "beach-best5",
    category: "Drive",
    title: "初めての宮古島で行きたいビーチ5選",
    text: "与那覇前浜、砂山、17ENDなど、初めてでも行きやすく写真に残したいビーチを厳選。",
    date: "2026.07.22",
    readTime: "6分",
    image: beachImage,
    tags: ["#宮古島", "#ビーチ", "#映えスポット", "#撮影"],
  },
  {
    id: 11,
    slug: "rainy-day-plan",
    category: "Rainy Day",
    title: "雨の日でも楽しめる宮古島の室内体験",
    text: "レジンアート、シーサー作り、パーマネントジュエリー、カスタムアクセサリー作りを紹介。",
    date: "2026.07.22",
    readTime: "6分",
    image: rainyImage,
    tags: ["#宮古島", "#雨の日", "#室内体験", "#宮古島体験"],
  },
  {
    id: 12,
    slug: "drone-photo-spots",
    category: "Tour",
    title: "宮古島でドローン撮影したい絶景スポット5選",
    text: "宮古ブルーの海、橋、サンセットまで、空から残すと映えるスポットを紹介。",
    date: "2026.07.22",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#ドローン", "#撮影", "#映えスポット"],
  },
  {
    id: 13,
    slug: "jet-secret-beach-tour",
    category: "Tour",
    title: "ジェットでしか辿り着けない本当の宮古島に出会えるツアー",
    text: "ユニの浜とジェットでしか行けない秘境ビーチへ。ドローン撮影付きのプライベートツアーを紹介。",
    date: "2026.07.25",
    readTime: "5分",
    image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
    tags: [
      "#宮古島",
      "#ジェット",
      "#秘境",
      "#シークレットビーチ",
      "#マリンアクティビティ",
      "#ドローン",
      "#撮影",
    ],
  },
  {
    id: 27,
    slug: "one-day-photogenic-tour-experience",
    category: "Tour",
    title: "秘境フォトツアーの様子を紹介",
    text: "秘境スポット巡り、撮影、ドローン撮影まで。秘境フォトツアー当日の雰囲気を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp"),
    tags: ["#宮古島", "#秘境フォトツアー", "#ツアーの様子", "#ドローン"],
  },
  {
    id: 37,
    slug: "girls-trip-photo-movie-miyakojima",
    category: "Shooting",
    title: "宮古島女子旅で絶対に残したい写真と映像",
    text: "観光客が行かないSUPでしか来れない秘境やシークレットビーチで、SNSに載せたくなる写真・動画を残す旅。",
    date: "2026.08.08",
    readTime: "5分",
    image: img("v1785311438/DSC00522_vyppts.webp"),
    tags: [
      "#宮古島",
      "#宮古島女子旅",
      "#女子旅",
      "#撮影",
      "#秘境SUP",
      "#秘境",
      "#SUP",
      "#シークレットビーチ",
      "#完全プライベート",
      "#非日常体験",
      "#映え",
    ],
  },
  {
    id: 28,
    slug: "hidden-sup-tour-experience",
    category: "Tour",
    title: "秘境SUPツアーの様子を紹介",
    text: "人が少ない海でSUPを楽しむ、秘境SUPツアーの流れと撮影の雰囲気を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: img("v1784734130/DJI_20260722152642_0033_D_tb9qki.webp"),
    tags: ["#宮古島", "#SUP", "#秘境", "#ツアーの様子"],
  },
  {
    id: 29,
    slug: "jet-private-secret-tour-experience",
    category: "Tour",
    title: "ジェットツアーの様子を紹介",
    text: "ユニの浜とジェットでしか行けない秘境ビーチへ向かう、特別なツアーの様子を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
    tags: ["#宮古島", "#ジェット", "#秘境", "#ツアーの様子"],
  },
  {
    id: 30,
    slug: "ultimate-extraordinary-miyakojima",
    category: "Drive",
    title: "全てを忘れ究極の非日常体験を求める方へ",
    text: "秘境の海、絶景撮影、SUP、ジェット、星空まで。宮古島の究極の過ごし方を紹介。",
    date: "2026.07.31",
    readTime: "6分",
    image: bridgeImage,
    tags: ["#宮古島", "#非日常体験", "#秘境", "#秘境フォトツアー"],
  },
  {
    id: 31,
    slug: "yabiji-hidden-paradise",
    category: "Tour",
    title: "人生で一度は行きたい秘境・八重干瀬",
    text: "船で向かう特別な海、八重干瀬。透明度とサンゴ礁の魅力を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: img("v1785003708/DJI_20260707132223_0047_D_small_no9d5s.jpg"),
    tags: ["#宮古島", "#八重干瀬", "#秘境", "#マリンアクティビティ"],
  },
  {
    id: 32,
    slug: "how-to-visit-secret-beaches",
    category: "Tour",
    title: "まだ誰も行ったことがない秘境に行く方法",
    text: "観光客が少ない秘境やシークレットビーチへ、安全に行くための考え方を紹介。",
    date: "2026.07.31",
    readTime: "5分",
    image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
    tags: ["#宮古島", "#秘境", "#シークレットビーチ", "#非日常体験"],
  },
];

const popularArticleSlugs = [
  "girls-trip-photo-movie-miyakojima",
  "cafe",
  "bae-cafe-best5",
  "recommend-sata-andagi",
  "miyakojima-surfing",
];

export const categories = [
  {
    key: "all",
    label: { JP: "すべて", EN: "All", 中文: "全部", 한국어: "전체" },
    icon: "📚",
    path: "/blog",
  },
  {
    key: "girls",
    label: {
      JP: "女子旅",
      EN: "Girls Trip",
      中文: "女生旅行",
      한국어: "여자여행",
    },
    icon: "💎",
    path: "/blog/category/girls",
  },
  {
    key: "hotel",
    label: { JP: "ホテル", EN: "Hotel", 中文: "酒店", 한국어: "호텔" },
    icon: "🏨",
    path: "/blog/category/hotel",
  },
  {
    key: "cafe",
    label: { JP: "カフェ", EN: "Cafe", 中文: "咖啡馆", 한국어: "카페" },
    icon: "☕",
    path: "/blog/category/cafe",
  },
  {
    key: "gourmet",
    label: { JP: "グルメ", EN: "Food", 中文: "美食", 한국어: "맛집" },
    icon: "🍴",
    path: "/blog/category/gourmet",
  },
  {
    key: "dinner",
    label: { JP: "夜ご飯", EN: "Dinner", 中文: "晚餐", 한국어: "저녁식사" },
    icon: "🍽️",
    path: "/blog/category/dinner",
  },
  {
    key: "drive",
    label: { JP: "ドライブ", EN: "Drive", 中文: "自驾", 한국어: "드라이브" },
    icon: "🚙",
    path: "/blog/category/drive",
  },
  {
    key: "shooting",
    label: { JP: "撮影", EN: "Photo", 中文: "拍摄", 한국어: "촬영" },
    icon: "📷",
    path: "/blog/category/shooting",
  },
  {
    key: "star",
    label: { JP: "星空", EN: "Stars", 中文: "星空", 한국어: "별하늘" },
    icon: "🌌",
    path: "/blog/category/star",
  },
  {
    key: "tour",
    label: { JP: "ツアー", EN: "Tours", 中文: "行程", 한국어: "투어" },
    icon: "🏄",
    path: "/blog/category/tour",
  },
];

export const tagLinks = [
  {
    key: "宮古島",
    label: {
      JP: "#宮古島",
      EN: "#Miyakojima",
      中文: "#宫古岛",
      한국어: "#미야코지마",
    },
    path: "/blog/tag/宮古島",
  },
  {
    key: "女子旅",
    label: {
      JP: "#女子旅",
      EN: "#GirlsTrip",
      中文: "#女生旅行",
      한국어: "#여자여행",
    },
    path: "/blog/tag/女子旅",
  },
  {
    key: "宮古島女子旅",
    label: {
      JP: "#宮古島女子旅",
      EN: "#MiyakojimaGirlsTrip",
      中文: "#宫古岛女生旅行",
      한국어: "#미야코지마여자여행",
    },
    path: "/blog/tag/宮古島女子旅",
  },
  {
    key: "秘境SUP",
    label: {
      JP: "#秘境SUP",
      EN: "#HiddenSUP",
      中文: "#秘境SUP",
      한국어: "#비경SUP",
    },
    path: "/blog/tag/秘境SUP",
  },
  {
    key: "完全プライベート",
    label: {
      JP: "#完全プライベート",
      EN: "#PrivateTour",
      中文: "#完全私人行程",
      한국어: "#완전프라이빗",
    },
    path: "/blog/tag/完全プライベート",
  },
  {
    key: "観光マップに載ってない",
    label: {
      JP: "#観光マップに載ってない",
      EN: "#NotOnTouristMaps",
      中文: "#旅游地图上没有",
      한국어: "#관광지도에없는곳",
    },
    path: "/blog/tag/観光マップに載ってない",
  },
  {
    key: "大人の海遊び",
    label: {
      JP: "#大人の海遊び",
      EN: "#RefinedOceanExperience",
      中文: "#成熟海岛体验",
      한국어: "#어른의바다체험",
    },
    path: "/blog/tag/大人の海遊び",
  },
  {
    key: "一番綺麗な海",
    label: {
      JP: "#一番綺麗な海",
      EN: "#ClearestOcean",
      中文: "#最美大海",
      한국어: "#가장아름다운바다",
    },
    path: "/blog/tag/一番綺麗な海",
  },
  {
    key: "ホテル",
    label: {
      JP: "#ホテル",
      EN: "#Hotel",
      中文: "#酒店",
      한국어: "#호텔",
    },
    path: "/blog/tag/ホテル",
  },
  {
    key: "カフェ巡り",
    label: {
      JP: "#カフェ巡り",
      EN: "#CafeHopping",
      中文: "#咖啡馆巡游",
      한국어: "#카페투어",
    },
    path: "/blog/tag/カフェ巡り",
  },
  {
    key: "伊良部島",
    label: {
      JP: "#伊良部島",
      EN: "#IrabuIsland",
      中文: "#伊良部岛",
      한국어: "#이라부섬",
    },
    path: "/blog/tag/伊良部島",
  },
  {
    key: "来間島",
    label: {
      JP: "#来間島",
      EN: "#KurimaIsland",
      中文: "#来间岛",
      한국어: "#구리마섬",
    },
    path: "/blog/tag/来間島",
  },
  {
    key: "池間島",
    label: {
      JP: "#池間島",
      EN: "#IkemaIsland",
      中文: "#池间岛",
      한국어: "#이케마섬",
    },
    path: "/blog/tag/池間島",
  },
  {
    key: "グルメ",
    label: { JP: "#グルメ", EN: "#Food", 中文: "#美食", 한국어: "#맛집" },
    path: "/blog/tag/グルメ",
  },
  {
    key: "ランチ",
    label: { JP: "#ランチ", EN: "#Lunch", 中文: "#午餐", 한국어: "#점심" },
    path: "/blog/tag/ランチ",
  },
  {
    key: "宮古そば",
    label: {
      JP: "#宮古そば",
      EN: "#MiyakoSoba",
      中文: "#宫古荞麦面",
      한국어: "#미야코소바",
    },
    path: "/blog/tag/宮古そば",
  },
  {
    key: "ビーチ",
    label: { JP: "#ビーチ", EN: "#Beach", 中文: "#海滩", 한국어: "#해변" },
    path: "/blog/tag/ビーチ",
  },
  {
    key: "モデルコース",
    label: {
      JP: "#モデルコース",
      EN: "#Itinerary",
      中文: "#行程",
      한국어: "#여행코스",
    },
    path: "/blog/tag/モデルコース",
  },
  {
    key: "サーターアンダギー",
    label: {
      JP: "#サーターアンダギー",
      EN: "#SataAndagi",
      中文: "#萨塔安达吉",
      한국어: "#사타안다기",
    },
    path: "/blog/tag/サーターアンダギー",
  },
  {
    key: "ドライブ",
    label: {
      JP: "#ドライブ",
      EN: "#Drive",
      中文: "#自驾",
      한국어: "#드라이브",
    },
    path: "/blog/tag/ドライブ",
  },
  {
    key: "レンタカー",
    label: {
      JP: "#レンタカー",
      EN: "#RentACar",
      中文: "#租车",
      한국어: "#렌터카",
    },
    path: "/blog/tag/レンタカー",
  },
  {
    key: "格安レンタカー",
    label: {
      JP: "#格安レンタカー",
      EN: "#CheapRentACar",
      中文: "#平价租车",
      한국어: "#저렴한렌터카",
    },
    path: "/blog/tag/格安レンタカー",
  },
  {
    key: "サンセット",
    label: { JP: "#サンセット", EN: "#Sunset", 中文: "#夕阳", 한국어: "#선셋" },
    path: "/blog/tag/サンセット",
  },
  {
    key: "映えスポット",
    label: {
      JP: "#映えスポット",
      EN: "#Photogenic",
      中文: "#拍照打卡",
      한국어: "#사진스팟",
    },
    path: "/blog/tag/映えスポット",
  },
  {
    key: "映え",
    label: {
      JP: "#映え",
      EN: "#Photogenic",
      中文: "#拍照好看",
      한국어: "#인생샷",
    },
    path: "/blog/tag/映え",
  },
  {
    key: "絶景スポット",
    label: {
      JP: "#絶景スポット",
      EN: "#ScenicSpot",
      中文: "#绝景景点",
      한국어: "#절경스팟",
    },
    path: "/blog/tag/絶景スポット",
  },
  {
    key: "秘境フォトツアー",
    label: {
      JP: "#秘境フォトツアー",
      EN: "#ScenicTour",
      中文: "#绝景行程",
      한국어: "#절경투어",
    },
    path: "/blog/tag/秘境フォトツアー",
  },
  {
    key: "SUP",
    label: { JP: "#SUP", EN: "#SUP", 中文: "#SUP", 한국어: "#SUP" },
    path: "/blog/tag/SUP",
  },
  {
    key: "ジェット",
    label: {
      JP: "#ジェット",
      EN: "#JetSki",
      中文: "#摩托艇",
      한국어: "#제트스키",
    },
    path: "/blog/tag/ジェット",
  },
  {
    key: "秘境",
    label: {
      JP: "#秘境",
      EN: "#HiddenBeach",
      中文: "#秘境",
      한국어: "#비밀스팟",
    },
    path: "/blog/tag/秘境",
  },
  {
    key: "シークレットビーチ",
    label: {
      JP: "#シークレットビーチ",
      EN: "#SecretBeach",
      中文: "#秘密海滩",
      한국어: "#시크릿비치",
    },
    path: "/blog/tag/シークレットビーチ",
  },
  {
    key: "マリンアクティビティ",
    label: {
      JP: "#マリンアクティビティ",
      EN: "#MarineActivity",
      中文: "#海上活动",
      한국어: "#해양액티비티",
    },
    path: "/blog/tag/マリンアクティビティ",
  },
  {
    key: "サーフィン",
    label: {
      JP: "#サーフィン",
      EN: "#Surfing",
      中文: "#冲浪",
      한국어: "#서핑",
    },
    path: "/blog/tag/サーフィン",
  },
  {
    key: "ドローン",
    label: { JP: "#ドローン", EN: "#Drone", 中文: "#无人机", 한국어: "#드론" },
    path: "/blog/tag/ドローン",
  },
  {
    key: "撮影",
    label: { JP: "#撮影", EN: "#Photography", 中文: "#拍摄", 한국어: "#촬영" },
    path: "/blog/tag/撮影",
  },
  {
    key: "夜ご飯",
    label: {
      JP: "#夜ご飯",
      EN: "#Dinner",
      中文: "#晚餐",
      한국어: "#저녁식사",
    },
    path: "/blog/tag/夜ご飯",
  },
  {
    key: "夜遊び",
    label: {
      JP: "#夜遊び",
      EN: "#Nightlife",
      中文: "#夜生活",
      한국어: "#밤여행",
    },
    path: "/blog/tag/夜遊び",
  },
  {
    key: "フルムーンパーティ",
    label: {
      JP: "#フルムーンパーティ",
      EN: "#FullMoonParty",
      中文: "#满月派对",
      한국어: "#풀문파티",
    },
    path: "/blog/tag/フルムーンパーティ",
  },
  {
    key: "焼肉",
    label: {
      JP: "#焼肉",
      EN: "#Yakiniku",
      中文: "#烤肉",
      한국어: "#야키니쿠",
    },
    path: "/blog/tag/焼肉",
  },
  {
    key: "宮古牛",
    label: {
      JP: "#宮古牛",
      EN: "#MiyakoBeef",
      中文: "#宫古牛",
      한국어: "#미야코규",
    },
    path: "/blog/tag/宮古牛",
  },
  {
    key: "八重干瀬",
    label: {
      JP: "#八重干瀬",
      EN: "#Yabiji",
      中文: "#八重干濑",
      한국어: "#야비지",
    },
    path: "/blog/tag/八重干瀬",
  },
  {
    key: "星空撮影",
    label: {
      JP: "#星空撮影",
      EN: "#StarPhotography",
      中文: "#星空拍摄",
      한국어: "#별사진촬영",
    },
    path: "/blog/tag/星空撮影",
  },
  {
    key: "星空",
    label: {
      JP: "#星空",
      EN: "#Stars",
      中文: "#星空",
      한국어: "#별하늘",
    },
    path: "/blog/tag/星空",
  },
  {
    key: "星空スポット",
    label: {
      JP: "#星空スポット",
      EN: "#StargazingSpot",
      中文: "#观星地点",
      한국어: "#별보기스팟",
    },
    path: "/blog/tag/星空スポット",
  },
  {
    key: "天の川",
    label: {
      JP: "#天の川",
      EN: "#MilkyWay",
      中文: "#银河",
      한국어: "#은하수",
    },
    path: "/blog/tag/天の川",
  },
  {
    key: "新月",
    label: {
      JP: "#新月",
      EN: "#NewMoon",
      中文: "#新月",
      한국어: "#신월",
    },
    path: "/blog/tag/新月",
  },
  {
    key: "ジェラート",
    label: {
      JP: "#ジェラート",
      EN: "#Gelato",
      中文: "#意式冰淇淋",
      한국어: "#젤라토",
    },
    path: "/blog/tag/ジェラート",
  },
  {
    key: "スイーツ",
    label: {
      JP: "#スイーツ",
      EN: "#Sweets",
      中文: "#甜点",
      한국어: "#디저트",
    },
    path: "/blog/tag/スイーツ",
  },
  {
    key: "宮古島スイーツ",
    label: {
      JP: "#宮古島スイーツ",
      EN: "#MiyakojimaSweets",
      中文: "#宫古岛甜点",
      한국어: "#미야코지마디저트",
    },
    path: "/blog/tag/宮古島スイーツ",
  },
  {
    key: "雨の日",
    label: {
      JP: "#雨の日",
      EN: "#RainyDay",
      中文: "#雨天",
      한국어: "#비오는날",
    },
    path: "/blog/tag/雨の日",
  },
  {
    key: "室内体験",
    label: {
      JP: "#室内体験",
      EN: "#IndoorActivity",
      中文: "#室内体验",
      한국어: "#실내체험",
    },
    path: "/blog/tag/室内体験",
  },
  {
    key: "宮古島体験",
    label: {
      JP: "#宮古島体験",
      EN: "#MiyakojimaActivity",
      中文: "#宫古岛体验",
      한국어: "#미야코지마체험",
    },
    path: "/blog/tag/宮古島体験",
  },
  {
    key: "旅動画",
    label: {
      JP: "#旅動画",
      EN: "#TravelVideo",
      中文: "#旅行视频",
      한국어: "#여행영상",
    },
    path: "/blog/tag/旅動画",
  },
  {
    key: "ツアーの様子",
    label: {
      JP: "#ツアーの様子",
      EN: "#TourExperience",
      中文: "#行程体验",
      한국어: "#투어후기",
    },
    path: "/blog/tag/ツアーの様子",
  },
  {
    key: "非日常体験",
    label: {
      JP: "#非日常体験",
      EN: "#ExtraordinaryExperience",
      中文: "#非日常体验",
      한국어: "#비일상체험",
    },
    path: "/blog/tag/非日常体験",
  },
];

export const categoryArticles = {
  girls: [
    {
      id: "girls-1",
      slug: "girls-trip-photo-movie-miyakojima",
      category: "Girls Trip",
      title: "宮古島女子旅で絶対に残したい写真と映像",
      text: "宮古島 女子旅 写真で探している方へ。SUPでしか来れない秘境やシークレットビーチで、SNSに載せたくなる写真・動画を残す旅。",
      date: "2026.08.08",
      readTime: "5分",
      image: img("v1786120675/DSC00888_tk8uhe.webp"),
      tags: [
        "#宮古島",
        "#宮古島女子旅",
        "#女子旅",
        "#撮影",
        "#秘境SUP",
        "#シークレットビーチ",
        "#完全プライベート",
        "#非日常体験",
      ],
    },
    {
      id: "girls-2",
      slug: "girls-trip-model-course-2n3d",
      category: "Girls Trip",
      title: "宮古島で映え女子旅！ 2泊3日のモデルコース",
      text: "カフェ、ビーチ、サンセット、秘境SUPまで。宮古島 女子旅 映えスポットを無理なく巡るモデルコース。",
      date: "2026.08.08",
      readTime: "6分",
      image: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp"),
      tags: [
        "#宮古島",
        "#宮古島女子旅",
        "#女子旅",
        "#モデルコース",
        "#映えスポット",
        "#撮影",
      ],
    },
    {
      id: "girls-3",
      slug: "miyakojima-girls-trip-spots",
      category: "Girls Trip",
      title: "宮古島周辺でおすすめの女子旅スポット",
      text: "SNS映えする海、カフェ、サンセット、観光マップに載っていない場所まで女子旅向けに紹介。",
      date: "2026.08.08",
      readTime: "6分",
      image: beachImage,
      tags: [
        "#宮古島",
        "#宮古島女子旅",
        "#女子旅",
        "#映えスポット",
        "#観光マップに載ってない",
      ],
    },
    {
      id: "girls-4",
      slug: "hidden-sup-tour-experience",
      category: "Girls Trip",
      title: "SUPでしか来れない秘境SUPツアーの様子",
      text: "完全プライベートで楽しむ、少し大人な海遊び。宮古島 秘境SUPの雰囲気を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1785005393/DSC00191_small_lhfvnk.jpg"),
      tags: [
        "#宮古島",
        "#秘境SUP",
        "#SUP",
        "#大人の海遊び",
        "#完全プライベート",
        "#非日常体験",
      ],
    },
  ],

  cafe: [
    {
      id: "cafe-1",
      slug: "cafe",
      category: "Cafe",
      title: "宮古島のおすすめ厳選カフェ",
      text: "海が見える絶景カフェ、緑に囲まれたカフェ、雨の日でも楽しめるカフェを紹介。",
      date: "2026.06.27",
      readTime: "8分",
      image: cafeImage,
      tags: ["#宮古島", "#カフェ巡り", "#映えスポット"],
    },
    {
      id: "cafe-4",
      slug: "bae-cafe-best5",
      category: "Cafe",
      title: "写真撮るならここ！映えるカフェBEST6",
      text: "宮古島で写真や動画に残したい、海・緑・南国感がきれいに映えるカフェを厳選。",
      date: "2026.07.22",
      readTime: "6分",
      image: cafeImage,
      tags: ["#宮古島", "#カフェ巡り", "#映えスポット", "#撮影"],
    },
    {
      id: "cafe-6",
      slug: "cafe-by-area",
      category: "Cafe",
      title: "場所別で探す宮古島のおすすめカフェ",
      text: "伊良部島、来間島、池間島、宮古島本島のエリア別に、ドライブ途中で立ち寄りやすいカフェを紹介。",
      date: "2026.07.25",
      readTime: "7分",
      image: cafeImage,
      tags: ["#宮古島", "#カフェ巡り", "#伊良部島", "#来間島", "#池間島"],
    },
  ],

  rainy: [
    {
      id: "rainy-1",
      slug: "rainy-day-plan",
      category: "Rainy Day",
      title: "雨の日でも楽しめる宮古島の室内体験",
      text: "レジンアート、シーサー作り、パーマネントジュエリー、カスタムアクセサリー作りを紹介。",
      date: "2026.07.22",
      readTime: "6分",
      image: rainyImage,
      tags: ["#宮古島", "#雨の日", "#室内体験", "#宮古島体験"],
    },
  ],

  sunset: [
    {
      id: "sunset-1",
      slug: "sunset-spots-best5",
      category: "Sunset",
      title: "宮古島のおすすめサンセットスポット5選",
      text: "17END、佐和田の浜、トゥリバーなど、夕日が綺麗に見える場所を紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: img("v1784466423/DJI_20260718160638_0015_D_gh8w0n.webp"),
      tags: ["#宮古島", "#サンセット", "#撮影", "#旅動画"],
    },
  ],

  gourmet: [
    {
      id: "gourmet-1",
      slug: "recommend-lunch",
      category: "Gourmet",
      title: "宮古島のおすすめランチ・グルメ",
      text: "カレー、島食材ランチ、カフェごはん、肉料理まで。宮古島旅行で立ち寄りたいおすすめランチを紹介。",
      date: "2026.06.28",
      readTime: "7分",
      image: lunchImage,
      tags: ["#宮古島", "#グルメ", "#ランチ"],
    },
    {
      id: "gourmet-2",
      slug: "recommend-soba",
      category: "Gourmet",
      title: "宮古そばの名店5選",
      text: "市街地で行きやすいお店から、北部ドライブ、南部方面で立ち寄りたいお店まで紹介。",
      date: "2026.06.28",
      readTime: "7分",
      image: sobaImage,
      tags: ["#宮古島", "#グルメ", "#宮古そば", "#ランチ"],
    },
    {
      id: "gourmet-3",
      slug: "recommend-sata-andagi",
      category: "Gourmet",
      title: "宮古島でサーターアンダギー食べるならここ5選",
      text: "なかゆくい商店、ななみカフェ、カフェ オルオキ、海美来、七福さたぱんびん。旅のおやつに立ち寄りたい5店を紹介。",
      date: "2026.06.28",
      readTime: "6分",
      image: sataAndagiImage,
      tags: ["#宮古島", "#グルメ", "#サーターアンダギー", "#お土産"],
    },
    {
      id: "gourmet-8",
      slug: "miyakojima-night-dinner-guide",
      category: "Gourmet",
      title: "宮古島のおすすめ夜ご飯｜雰囲気別ガイド",
      text: "島おでん、民謡居酒屋、海鮮、宮古牛、しゃぶしゃぶまで。営業時間・定休日・価格帯つきで紹介。",
      date: "2026.09.10",
      readTime: "8分",
      image: nightDinnerGuideImage,
      tags: ["#宮古島", "#宮古島グルメ", "#夜ご飯", "#居酒屋"],
    },
    {
      id: "gourmet-4",
      slug: "night-dinner-top5",
      category: "Gourmet",
      title: "宮古島の夜ご飯TOP5",
      text: "島おでん、民謡居酒屋、ローカル居酒屋、宮古牛まで。宮古島旅行の夜に行きたいお店を厳選。",
      date: "2026.07.31",
      readTime: "6分",
      image: nightDinnerGuideImage,
      tags: ["#宮古島", "#グルメ", "#夜ご飯", "#夜遊び"],
    },
    {
      id: "gourmet-6",
      slug: "miyakojima-yakiniku-top3",
      category: "Gourmet",
      title: "宮古島焼肉店TOP3",
      text: "宮古牛を楽しめる焼肉店を、旅行中に選びやすい価格帯の目安つきで紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: nightDinnerGuideImage,
      tags: ["#宮古島", "#グルメ", "#焼肉", "#宮古牛"],
    },
    {
      id: "gourmet-7",
      slug: "miyakojima-sweets-best14",
      category: "Gourmet",
      title: "宮古島スイーツ特集 14選",
      text: "スムージー、マンゴー、さたぱんびん、アイス、ドーナツまで。宮古島旅行で立ち寄りたいスイーツ店を紹介。",
      date: "2026.07.31",
      readTime: "8分",
      image: sataAndagiImage,
      tags: ["#宮古島", "#グルメ", "#スイーツ", "#宮古島スイーツ"],
    },
    {
      id: "gourmet-5",
      slug: "miyakojima-gelato-best5",
      category: "Gourmet",
      title: "宮古島でジェラート食べるならここ5選",
      text: "リッコジェラート、Ninufa、ブルーシールなど、宮古島旅行中に立ち寄りたい冷たいスイーツを紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: gelatoImage,
      tags: ["#宮古島", "#グルメ", "#ジェラート", "#スイーツ"],
    },
  ],

  dinner: [
    {
      id: "dinner-3",
      slug: "miyakojima-night-dinner-guide",
      category: "Dinner",
      title: "宮古島のおすすめ夜ご飯｜雰囲気別ガイド",
      text: "島おでん、民謡居酒屋、海鮮、宮古牛、しゃぶしゃぶまで。営業時間・定休日・価格帯つきで紹介。",
      date: "2026.09.10",
      readTime: "8分",
      image: dinnerImage,
      tags: ["#宮古島", "#宮古島グルメ", "#夜ご飯", "#居酒屋"],
    },
    {
      id: "dinner-1",
      slug: "night-dinner-top5",
      category: "Dinner",
      title: "宮古島の夜ご飯TOP5",
      text: "島おでん、民謡居酒屋、ローカル居酒屋、宮古牛まで。宮古島旅行の夜に行きたいお店を厳選。",
      date: "2026.07.31",
      readTime: "6分",
      image: dinnerImage,
      tags: ["#宮古島", "#グルメ", "#夜ご飯", "#夜遊び"],
    },
    {
      id: "dinner-2",
      slug: "miyakojima-yakiniku-top3",
      category: "Dinner",
      title: "宮古島焼肉店TOP3",
      text: "宮古牛を楽しめる焼肉店を、旅行中に選びやすい価格帯の目安つきで紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: dinnerImage,
      tags: ["#宮古島", "#グルメ", "#夜ご飯", "#焼肉", "#宮古牛"],
    },
  ],

  hotel: [
    {
      id: "hotel-guide-by-area-1",
      slug: "miyako-hotel-guide-by-area",
      category: "Hotel",
      title: "【エリア別】宮古島のおすすめホテル・リゾート",
      text: "伊良部島、シギラ・上野、平良・北部、来間島、与那覇前浜周辺までエリア別に紹介。",
      date: "2026.09.08",
      readTime: "10分",
      image: hotelGuideImage,
      tags: ["#宮古島", "#宮古島ホテル", "#リゾート", "#ヴィラ"],
    },
  ],

  spot: [
    {
      id: "spot-girls-hotel-1",
      slug: "miyakojima-girls-trip-hotels",
      category: "Spot",
      title: "宮古島で女子旅におすすめのホテル6選",
      text: "写真映え、プール、オーシャンビュー、アクセスで選ぶ女子旅向けホテル。",
      date: "2026.08.08",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#女子旅", "#ホテル", "#非日常体験"],
    },
    {
      id: "spot-girls-spot-1",
      slug: "miyakojima-girls-trip-spots",
      category: "Spot",
      title: "宮古島周辺でおすすめの女子旅スポット",
      text: "ビーチ、橋、カフェ、サンセット、秘境SUPまで女子旅で行きたい場所を紹介。",
      date: "2026.08.08",
      readTime: "6分",
      image: img("v1784945639/DSC00440-2_syxyio.webp"),
      tags: ["#宮古島", "#女子旅", "#映えスポット", "#カフェ巡り", "#撮影"],
    },
  ],

  drive: [
    {
      id: "drive-girls-trip-1",
      slug: "girls-trip-model-course-2n3d",
      category: "Drive",
      title: "宮古島で映え女子旅！ 2泊3日のモデルコース",
      text: "写真・カフェ・ビーチ・サンセット・夜ご飯まで楽しむ女子旅モデルコース。",
      date: "2026.08.08",
      readTime: "6分",
      image: img("v1785776523/DSC00815_yry3re.webp"),
      tags: ["#宮古島", "#女子旅", "#モデルコース", "#映えスポット", "#撮影"],
    },
    {
      id: "5",
      slug: "drive-scenic-spots",
      category: "Drive",
      title: "宮古島で車で行ける絶景スポット9選",
      text: "ドライブで行ける絶景から、サンセットが綺麗に見える場所まで。宮古島旅行で立ち寄りたいスポットを紹介。",
      date: "2026.06.20",
      readTime: "7分",
      image: img("v1782544346/DJI_20260619092553_0013_D_mggybx.jpg"),
      tags: [
        "#宮古島",
        "#ドライブ",
        "#サンセット",
        "#映えスポット",
        "#ドローン",
      ],
    },
    {
      id: "drive-7",
      slug: "miyakojima-superb-view-spots",
      category: "Drive",
      title: "宮古島旅行で絶対に行きたい絶景スポット",
      text: "王道の絶景から秘境スポット、究極の非日常体験を残せる秘境フォトツアーまで紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#絶景スポット", "#撮影", "#ドローン"],
    },
    {
      id: "drive-8",
      slug: "miyakojima-cheap-local-rentacar",
      category: "Drive",
      title: "宮古島 激安ローカルレンタカーまとめ",
      text: "宮古島で安く借りたい人向けに、ローカル系レンタカーと選ぶ時の注意点を紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#レンタカー", "#格安レンタカー", "#ドライブ"],
    },
    {
      id: "drive-9",
      slug: "ultimate-extraordinary-miyakojima",
      category: "Drive",
      title: "全てを忘れ究極の非日常体験を求める方へ",
      text: "秘境の海、絶景撮影、SUP、ジェット、星空まで。宮古島の究極の過ごし方を紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#非日常体験", "#秘境", "#秘境フォトツアー"],
    },
    {
      id: "drive-10",
      slug: "miyakojima-today-travel-dashboard",
      category: "Drive",
      title: "宮古島を120%楽しむために！今日チェックしたい旅情報",
      text: "天気、日の出・日の入り、月の出・月入り、潮位、星空指数など出発前に見る情報を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: bridgeImage,
      tags: ["#宮古島", "#天気", "#潮位", "#星空"],
    },
    {
      id: "drive-3",
      slug: "model-course-2n3d",
      category: "Drive",
      title: "宮古島2泊3日モデルプラン",
      text: "秘境フォトツアーや秘境SUP、八重干瀬、サンセット、夜ご飯まで楽しむ2泊3日プラン。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#ドライブ", "#モデルコース", "#八重干瀬"],
    },
    {
      id: "drive-6",
      slug: "model-course-3n4d",
      category: "Drive",
      title: "宮古島3泊4日モデルプラン",
      text: "秘境フォトツアー、秘境SUP、八重干瀬、星空撮影まで無理なく楽しむ3泊4日プラン。",
      date: "2026.07.31",
      readTime: "7分",
      image: bridgeImage,
      tags: ["#宮古島", "#ドライブ", "#モデルコース", "#星空撮影"],
    },
    {
      id: "drive-4",
      slug: "without-car-plan",
      category: "Drive",
      title: "宮古島で車なしでも楽しむ1日プラン",
      text: "レンタカーなしでも楽しみたい方向けに、市街地・近場ビーチ・カフェを中心にした過ごし方を紹介。",
      date: "2026.07.22",
      readTime: "5分",
      image: rainyImage,
      tags: ["#宮古島", "#ドライブ", "#モデルコース", "#カフェ巡り"],
    },
    {
      id: "drive-5",
      slug: "beach-best5",
      category: "Drive",
      title: "初めての宮古島で行きたいビーチ5選",
      text: "与那覇前浜、砂山、17ENDなど、初めてでも行きやすく写真に残したいビーチを厳選。",
      date: "2026.07.22",
      readTime: "6分",
      image: beachImage,
      tags: ["#宮古島", "#ビーチ", "#映えスポット", "#撮影"],
    },
  ],

  tour: [
    {
      id: "tour-9",
      slug: "miyakojima-surfing-guide",
      category: "Tour",
      title: "宮古島来たらサーフィンしないと！",
      text: "サンライズ、初心者レッスン、多言語対応、ローカルガイドの安心感まで。宮古島サーフィン体験を動画で紹介。",
      date: "2026.09.01",
      readTime: "5分",
      image: img("v1788264796/IMG_6101_wqf7cj.jpg"),
      tags: [
        "#宮古島",
        "#宮古島サーフィン",
        "#サーフィン",
        "#マリンアクティビティ",
        "#ローカルガイド",
        "#多言語対応",
        "#撮影",
      ],
    },
    {
      id: "tour-8",
      slug: "girls-trip-photo-movie-miyakojima",
      category: "Tour",
      title: "宮古島女子旅で絶対に残したい写真と映像",
      text: "宮古島 女子旅 写真で探している方へ。SUPでしか来れない秘境やシークレットビーチで、SNSに載せたくなる写真・動画を残す旅。",
      date: "2026.08.08",
      readTime: "5分",
      image: img("v1786120675/DSC00888_tk8uhe.webp"),
      tags: [
        "#宮古島",
        "#宮古島女子旅",
        "#女子旅",
        "#撮影",
        "#秘境SUP",
        "#秘境",
        "#SUP",
        "#シークレットビーチ",
        "#完全プライベート",
        "#非日常体験",
        "#映え",
      ],
    },
    {
      id: "tour-3",
      slug: "jet-secret-beach-tour",
      category: "Tour",
      title: "ジェットでしか辿り着けない本当の宮古島に出会えるツアー",
      text: "ユニの浜とジェットでしか行けない秘境ビーチへ。ドローン撮影付きのプライベートツアーを紹介。",
      date: "2026.07.25",
      readTime: "5分",
      image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
      tags: [
        "#宮古島",
        "#ジェット",
        "#秘境",
        "#シークレットビーチ",
        "#マリンアクティビティ",
        "#ドローン",
        "#撮影",
      ],
    },
    {
      id: "tour-1",
      slug: "miyakojima-surfing",
      category: "Tour",
      title: "宮古島でサーフィンをするべき理由3選",
      text: "人が少ない海、宮古ブルーでの撮影、ローカルガイド付きの安心感。宮古島サーフィンの魅力を紹介。",
      date: "2026.07.19",
      readTime: "5分",
      image: img(
        "v1784354836/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-07-18_15.06.59_noluwe.png",
      ),
      tags: [
        "#宮古島",
        "#サーフィン",
        "#マリンアクティビティ",
        "#撮影",
        "#ドローン",
      ],
    },
    {
      id: "tour-4",
      slug: "miyakojima-surfing-experience",
      category: "Tour",
      title: "宮古島サーフィン体験の様子",
      text: "朝の海、レッスン、波に乗る瞬間、写真・映像データのお渡しまで、実際の動画と写真で紹介。",
      date: "2026.07.27",
      readTime: "4分",
      image: img("v1785121770/IMG_5983_abqsgh.webp"),
      tags: [
        "#宮古島",
        "#サーフィン",
        "#マリンアクティビティ",
        "#撮影",
        "#ドローン",
        "#旅動画",
      ],
    },
    {
      id: "tour-2",
      slug: "drone-photo-spots",
      category: "Tour",
      title: "宮古島でドローン撮影したい絶景スポット5選",
      text: "宮古ブルーの海、橋、サンセットまで、空から残すと映えるスポットを紹介。",
      date: "2026.07.22",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#ドローン", "#撮影", "#映えスポット"],
    },
    {
      id: "tour-5",
      slug: "one-day-photogenic-tour-experience",
      category: "Tour",
      title: "秘境フォトツアーの様子を紹介",
      text: "秘境スポット巡り、撮影、ドローン撮影まで。秘境フォトツアー当日の雰囲気を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp"),
      tags: ["#宮古島", "#秘境フォトツアー", "#ツアーの様子", "#ドローン"],
    },
    {
      id: "tour-6",
      slug: "hidden-sup-tour-experience",
      category: "Tour",
      title: "秘境SUPツアーの様子を紹介",
      text: "人が少ない海でSUPを楽しむ、秘境SUPツアーの流れと撮影の雰囲気を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1784734130/DJI_20260722152642_0033_D_tb9qki.webp"),
      tags: ["#宮古島", "#SUP", "#秘境", "#ツアーの様子"],
    },
    {
      id: "tour-7",
      slug: "jet-private-secret-tour-experience",
      category: "Tour",
      title: "ジェットツアーの様子を紹介",
      text: "ユニの浜とジェットでしか行けない秘境ビーチへ向かう、特別なツアーの様子を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
      tags: ["#宮古島", "#ジェット", "#秘境", "#ツアーの様子"],
    },
    {
      id: "tour-8",
      slug: "yabiji-hidden-paradise",
      category: "Tour",
      title: "人生で一度は行きたい秘境・八重干瀬",
      text: "船で向かう特別な海、八重干瀬。透明度とサンゴ礁の魅力を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1785003708/DJI_20260707132223_0047_D_small_no9d5s.jpg"),
      tags: ["#宮古島", "#八重干瀬", "#秘境", "#マリンアクティビティ"],
    },
    {
      id: "tour-9",
      slug: "how-to-visit-secret-beaches",
      category: "Tour",
      title: "まだ誰も行ったことがない秘境に行く方法",
      text: "観光客が少ない秘境やシークレットビーチへ、安全に行くための考え方を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
      tags: ["#宮古島", "#秘境", "#シークレットビーチ", "#非日常体験"],
    },
    {
      id: "tour-10",
      slug: "miyakojima-starry-sky-spots",
      category: "Tour",
      title: "宮古島で満天の星空を見るには？おすすめ星空スポット",
      text: "新月前後、月明かり、街灯の少ない場所など、満天の星空を見る条件とスポットを紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: starImage,
      tags: ["#宮古島", "#星空", "#星空スポット", "#星空撮影"],
    },
  ],

  star: [
    {
      id: "star-1",
      slug: "miyakojima-starry-sky-spots",
      category: "Star",
      title: "宮古島で満天の星空を見るには？おすすめ星空スポット",
      text: "新月前後、月明かり、街灯の少ない場所など、満天の星空を見る条件とスポットを紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: starImage,
      tags: ["#宮古島", "#星空", "#星空スポット", "#星空撮影"],
    },
    {
      id: "star-2",
      slug: "miyakojima-starry-season-2026",
      category: "Star",
      title:
        "宮古島の星空が綺麗に見える時期は？おすすめ日程・天の川情報【2026年版】",
      text: "新月前後、月明かり、天の川シーズン、2026年の星空フォトおすすめ日程を紹介。",
      date: "2026.07.31",
      readTime: "8分",
      image: starImage,
      tags: ["#宮古島", "#星空", "#天の川", "#新月"],
    },
    {
      id: "star-3",
      slug: "miyakojima-today-travel-dashboard",
      category: "Star",
      title: "宮古島を120%楽しむために！今日チェックしたい旅情報",
      text: "天気、日の出・日の入り、月の出・月入り、潮位、星空指数など出発前に見る情報を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: bridgeImage,
      tags: ["#宮古島", "#天気", "#潮位", "#星空"],
    },
  ],

  night: [
    {
      id: "night-6",
      slug: "miyakojima-night-dinner-guide",
      category: "Night",
      title: "宮古島のおすすめ夜ご飯｜雰囲気別ガイド",
      text: "島おでん、民謡居酒屋、海鮮、宮古牛、しゃぶしゃぶまで。営業時間・定休日・価格帯つきで紹介。",
      date: "2026.09.10",
      readTime: "8分",
      image: dinnerImage,
      tags: ["#宮古島", "#宮古島グルメ", "#夜ご飯", "#居酒屋"],
    },
    {
      id: "night-1",
      slug: "full-moon-party-japan-miyakojima",
      category: "Night",
      title: "日本に存在するフルムーンパーティとは？",
      text: "満月の夜に白をまとって集う、宮古島らしい非日常ナイトイベントを紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: starImage,
      tags: ["#宮古島", "#フルムーンパーティ", "#夜遊び", "#非日常体験"],
    },
    {
      id: "night-2",
      slug: "night-dinner-top5",
      category: "Night",
      title: "宮古島の夜ご飯TOP5",
      text: "島おでん、民謡居酒屋、ローカル居酒屋、宮古牛まで。宮古島旅行の夜に行きたいお店を厳選。",
      date: "2026.07.31",
      readTime: "6分",
      image: dinnerImage,
      tags: ["#宮古島", "#グルメ", "#夜ご飯", "#夜遊び"],
    },
  ],

  shooting: [
    {
      id: "shooting-girls-trip-1",
      slug: "girls-trip-photo-movie-miyakojima",
      category: "Shooting",
      title: "宮古島女子旅で絶対に残したい写真と映像",
      text: "宮古島 女子旅 写真で探している方へ。SUPでしか来れない秘境やシークレットビーチで、SNSに載せたくなる写真・動画を残す旅。",
      date: "2026.08.08",
      readTime: "5分",
      image: img("v1786120675/DSC00888_tk8uhe.webp"),
      tags: [
        "#宮古島",
        "#宮古島女子旅",
        "#女子旅",
        "#撮影",
        "#秘境SUP",
        "#秘境",
        "#SUP",
        "#シークレットビーチ",
        "#完全プライベート",
        "#非日常体験",
        "#映え",
      ],
    },
    {
      id: "shooting-cafe-1",
      slug: "bae-cafe-best5",
      category: "Shooting",
      title: "写真撮るならここ！映えるカフェBEST6",
      text: "宮古島で写真や動画に残したい、海・緑・南国感がきれいに映えるカフェを厳選。",
      date: "2026.07.22",
      readTime: "6分",
      image: cafeImage,
      tags: ["#宮古島", "#カフェ巡り", "#映えスポット", "#撮影"],
    },
    {
      id: "shooting-drive-1",
      slug: "drive-scenic-spots",
      category: "Shooting",
      title: "宮古島で車で行ける絶景スポット9選",
      text: "ドライブで行ける絶景から、サンセットが綺麗に見える場所まで。宮古島旅行で立ち寄りたいスポットを紹介。",
      date: "2026.06.20",
      readTime: "7分",
      image: img("v1782544346/DJI_20260619092553_0013_D_mggybx.jpg"),
      tags: [
        "#宮古島",
        "#ドライブ",
        "#サンセット",
        "#映えスポット",
        "#ドローン",
        "#撮影",
      ],
    },
    {
      id: "shooting-drive-2",
      slug: "miyakojima-superb-view-spots",
      category: "Shooting",
      title: "宮古島旅行で絶対に行きたい絶景スポット",
      text: "王道の絶景から秘境スポット、究極の非日常体験を残せる秘境フォトツアーまで紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: [
        "#宮古島",
        "#絶景スポット",
        "#秘境フォトツアー",
        "#撮影",
        "#ドローン",
      ],
    },
    {
      id: "shooting-beach-1",
      slug: "beach-best5",
      category: "Shooting",
      title: "初めての宮古島で行きたいビーチ5選",
      text: "与那覇前浜、砂山、17ENDなど、初めてでも行きやすく写真に残したいビーチを厳選。",
      date: "2026.07.22",
      readTime: "6分",
      image: beachImage,
      tags: ["#宮古島", "#ビーチ", "#映えスポット", "#撮影"],
    },
    {
      id: "shooting-tour-1",
      slug: "drone-photo-spots",
      category: "Shooting",
      title: "宮古島でドローン撮影したい絶景スポット5選",
      text: "宮古ブルーの海、橋、サンセットまで、空から残すと映えるスポットを紹介。",
      date: "2026.07.22",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#ドローン", "#撮影", "#映えスポット"],
    },
    {
      id: "shooting-tour-2",
      slug: "miyakojima-surfing",
      category: "Shooting",
      title: "宮古島でサーフィンをするべき理由3選",
      text: "人が少ない海、宮古ブルーでの撮影、ローカルガイド付きの安心感。宮古島サーフィンの魅力を紹介。",
      date: "2026.07.19",
      readTime: "5分",
      image: img(
        "v1784354836/%E3%82%B9%E3%82%AF%E3%83%AA%E3%83%BC%E3%83%B3%E3%82%B7%E3%83%A7%E3%83%83%E3%83%88_2026-07-18_15.06.59_noluwe.png",
      ),
      tags: [
        "#宮古島",
        "#サーフィン",
        "#マリンアクティビティ",
        "#撮影",
        "#ドローン",
      ],
    },
    {
      id: "shooting-tour-4",
      slug: "miyakojima-surfing-experience",
      category: "Shooting",
      title: "宮古島サーフィン体験の様子",
      text: "朝の海、レッスン、波に乗る瞬間、写真・映像データのお渡しまで、実際の動画と写真で紹介。",
      date: "2026.07.27",
      readTime: "4分",
      image: img("v1785121770/IMG_5983_abqsgh.webp"),
      tags: [
        "#宮古島",
        "#サーフィン",
        "#マリンアクティビティ",
        "#撮影",
        "#ドローン",
        "#旅動画",
      ],
    },
    {
      id: "shooting-tour-3",
      slug: "jet-secret-beach-tour",
      category: "Shooting",
      title: "ジェットでしか辿り着けない本当の宮古島に出会えるツアー",
      text: "ユニの浜とジェットでしか行けない秘境ビーチへ。ドローン撮影付きのプライベートツアーを紹介。",
      date: "2026.07.25",
      readTime: "5分",
      image: img("v1784949980/DJI_20260724131535_0015_D_robale.webp"),
      tags: [
        "#宮古島",
        "#ジェット",
        "#秘境",
        "#シークレットビーチ",
        "#マリンアクティビティ",
        "#ドローン",
        "#撮影",
      ],
    },
    {
      id: "shooting-tour-5",
      slug: "one-day-photogenic-tour-experience",
      category: "Shooting",
      title: "秘境フォトツアーの様子を紹介",
      text: "秘境スポット巡り、撮影、ドローン撮影まで。秘境フォトツアー当日の雰囲気を紹介。",
      date: "2026.07.31",
      readTime: "5分",
      image: img("v1784944008/DJI_20260724142728_0046_D_fjvofb.webp"),
      tags: ["#宮古島", "#秘境フォトツアー", "#ツアーの様子", "#ドローン"],
    },
    {
      id: "shooting-drive-3",
      slug: "ultimate-extraordinary-miyakojima",
      category: "Shooting",
      title: "全てを忘れ究極の非日常体験を求める方へ",
      text: "秘境の海、絶景撮影、SUP、ジェット、星空まで。宮古島の究極の過ごし方を紹介。",
      date: "2026.07.31",
      readTime: "6分",
      image: bridgeImage,
      tags: ["#宮古島", "#非日常体験", "#秘境", "#秘境フォトツアー"],
    },
  ],
};

export default function Blog({ pathData }) {
  const { categoryKey, tagKey } = useParams();
  const { lang } = useLanguage();
  const t = blogText[lang] || blogText.JP;

  const localizedArticles = useMemo(
    () => localizeArticleList(articles.filter(isVisibleBlogArticle), lang),
    [lang],
  );
  const localizedCategories = useMemo(
    () => localizeCategoryItems(categories, lang),
    [lang],
  );
  const localizedTagLinks = useMemo(
    () => localizeTagItems(tagLinks.filter(isVisibleBlogTag), lang),
    [lang],
  );
  const localizedCategoryArticles = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(categoryArticles).map(([key, articleList]) => [
          key,
          localizeArticleList(articleList.filter(isVisibleBlogArticle), lang),
        ]),
      ),
    [lang],
  );
  const newArticles = useMemo(
    () => localizedArticles.slice(-5).reverse(),
    [localizedArticles],
  );
  const popularArticles = useMemo(
    () =>
      popularArticleSlugs
        .map((slug) =>
          localizedArticles.find((article) => article.slug === slug),
        )
        .filter(Boolean),
    [localizedArticles],
  );

  const [selectedCategoryKey, setSelectedCategoryKey] = useState(
    categoryKey || "all",
  );
  const [selectedTagKey, setSelectedTagKey] = useState(
    tagKey ? decodeURIComponent(tagKey) : "",
  );

  useEffect(() => {
    setSelectedCategoryKey(categoryKey || "all");
    setSelectedTagKey(tagKey ? decodeURIComponent(tagKey) : "");
  }, [categoryKey, tagKey]);

  const activeCategory = selectedCategoryKey;
  const activeTag = selectedTagKey;

  const allCategoryArticles = useMemo(() => {
    return Object.values(localizedCategoryArticles).flat();
  }, [localizedCategoryArticles]);

  const uniqueArticles = useMemo(() => {
    return uniqueArticlesBySlug(allCategoryArticles);
  }, [allCategoryArticles]);

  const activeCategoryData = useMemo(
    () =>
      localizedCategories.find((category) => category.key === activeCategory),
    [activeCategory, localizedCategories],
  );

  const selectedArticles = useMemo(() => {
    if (activeTag) {
      return uniqueArticles.filter((article) => {
        const articleTags = Array.isArray(article.tags) ? article.tags : [];

        return articleTags.some(
          (tag) => tag.replace("#", "") === activeTag.replace("#", ""),
        );
      });
    }

    if (activeCategory === "all") {
      return uniqueArticles;
    }

    return uniqueArticlesBySlug(localizedCategoryArticles[activeCategory] || []);
  }, [activeCategory, activeTag, localizedCategoryArticles, uniqueArticles]);

  const selectedLabel = activeTag
    ? `#${activeTag}`
    : `${activeCategoryData?.icon || "📚"} ${t.selectedCategory}`;

  const selectedTitle = activeTag
    ? `#${activeTag}${t.articleSuffix}`
    : `${getLocalizedValue(activeCategoryData?.label, lang) || "ブログ"}${
        t.articleSuffix
      }`;

  return (
    <div className={styles.root}>
      <div className={styles.breadcrumbWrap}>
        <Breadcrumb pathData={pathData} overlay />
      </div>

      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className={styles.heroContent}>
          <h1>
            {t.heroTitle[0]}
            <br />
            {t.heroTitle[1]}
          </h1>

          <p>{t.heroDescription}</p>

          <div className={styles.heroButtons}>
            <a
              href="#popular"
              onClick={() =>
                trackCtaClick({
                  label: "blog_hero_popular",
                  placement: "blog_hero",
                  destination: "#popular",
                  destinationType: "anchor",
                })
              }
            >
              {t.heroPopularButton}
            </a>
            <Link
              to="/tours"
              onClick={() =>
                trackCtaClick({
                  label: "blog_hero_tours",
                  placement: "blog_hero",
                  destination: "/tours",
                  destinationType: "internal_tour",
                })
              }
            >
              {t.heroToursButton}
            </Link>
          </div>
        </div>
      </section>

      <section id="popular" className={styles.section}>
        <SectionHeader label="POPULAR ARTICLES" title={t.popularArticles} />

        <div className={styles.articleGrid}>
          {popularArticles.map((article) => (
            <ArticleCard key={article.id} article={article} lang={lang} />
          ))}
        </div>
      </section>

      <section className={`${styles.section} ${styles.categorySection}`}>
        <div className={styles.categoryRow}>
          <div className={styles.categoryHeader}>
            <div>
              <p className={styles.sectionLabel}>{t.categoryLabel}</p>
              <h2>{t.categoryTitle}</h2>
            </div>
          </div>

          <div className={styles.categoryGrid}>
            {localizedCategories.map((category) => (
              <button
                key={category.key}
                type="button"
                className={`${styles.categoryCard} ${
                  !activeTag && activeCategory === category.key
                    ? styles.categoryCardActive
                    : ""
                }`}
                onClick={() => {
                  setSelectedCategoryKey(category.key);
                  setSelectedTagKey("");

                  trackEvent("blog_category_click", {
                    event_category: "blog",
                    event_label: category.key,
                    category_key: category.key,
                    destination: category.path,
                    placement: "blog_page_inline",
                  });
                }}
              >
                <span>{category.icon}</span>
                <strong>{category.label}</strong>
              </button>
            ))}
          </div>
        </div>

        <div className={styles.categoryResults}>
          <div className={styles.categoryResultsHeader}>
            <div>
              <p>{selectedLabel}</p>
              <h3>{selectedTitle}</h3>
            </div>

            <span>
              {selectedArticles.length} {t.articlesCount}
            </span>
          </div>

          {selectedArticles.length > 0 ? (
            <div className={styles.categoryArticleGrid}>
              {selectedArticles.map((article) => (
                <ArticleCard key={article.id} article={article} lang={lang} />
              ))}
            </div>
          ) : (
            <p className={styles.emptyText}>{t.emptyText}</p>
          )}
        </div>
      </section>

      <section className={`${styles.section} ${styles.tagSection}`}>
        <SectionHeader label={t.tagLabel} title={t.tagTitle} />

        <div className={styles.tagGrid}>
          {localizedTagLinks.map((tag) => (
            <Link
              key={tag.key}
              to={tag.path}
              className={
                activeTag === tag.key ? styles.tagCardActive : styles.tagCard
              }
              onClick={() =>
                trackEvent("blog_tag_click", {
                  event_category: "blog",
                  event_label: tag.key,
                  tag_key: tag.key,
                  destination: tag.path,
                  placement: "blog_page",
                })
              }
            >
              {tag.label}
            </Link>
          ))}
        </div>
      </section>

      <section className={styles.featured}>
        <div className={styles.featuredImage}>
          <img
            src={img("v1782544346/DJI_20260619092553_0013_D_mggybx.jpg")}
            alt="宮古島で車で行ける絶景スポット"
          />
        </div>

        <div className={styles.featuredText}>
          <p className={styles.sectionLabel}>{t.featuredLabel}</p>

          <h2>{t.featuredTitle}</h2>

          <p>{t.featuredText}</p>

          <div className={styles.tags}>
            <span>#ドライブ</span>
            <span>#宮古島旅行</span>
            <span>#サンセット</span>
            <span>#映えスポット</span>
          </div>

          <Link
            className={styles.primaryButton}
            to={`${ARTICLE_BASE_PATH}/drive-scenic-spots`}
            onClick={() =>
              trackEvent("blog_featured_article_click", {
                event_category: "blog",
                event_label: "drive-scenic-spots",
                article_slug: "drive-scenic-spots",
                destination: `${ARTICLE_BASE_PATH}/drive-scenic-spots`,
                placement: "blog_featured",
              })
            }
          >
            {t.featuredButton}
          </Link>
        </div>

        <div className={styles.mapCard}>
          <img src={img("v1783239689/map_hijdds.png")} alt="宮古島のマップ" />
        </div>
      </section>

      <section className={styles.section}>
        <SectionHeader label="NEW ARTICLES" title={t.newArticles} />

        <div className={styles.newGrid}>
          {newArticles.map((article) => (
            <Link
              key={article.id}
              to={`${ARTICLE_BASE_PATH}/${article.slug}`}
              className={styles.newCard}
              onClick={() =>
                trackEvent("blog_article_click", {
                  event_category: "blog",
                  event_label: article.slug,
                  article_slug: article.slug,
                  destination: `${ARTICLE_BASE_PATH}/${article.slug}`,
                  placement: "blog_new_articles",
                })
              }
            >
              <img src={article.image} alt={article.title} />

              <div>
                <h3>{article.title}</h3>
                <p>
                  {article.date}
                  <span>◷</span>
                  {article.readTime}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <BlogMemoryCta to="/tours/one-day-photogenic-tour" />
    </div>
  );
}

function SectionHeader({ label, title, linkText }) {
  return (
    <div className={styles.sectionHeader}>
      <div>
        <p className={styles.sectionLabel}>{label}</p>
        <h2>{title}</h2>
      </div>

      {linkText && (
        <Link
          to="/blog"
          onClick={() =>
            trackEvent("blog_archive_click", {
              event_category: "blog",
              event_label: label,
              destination: "/blog",
              placement: "blog_section_header",
            })
          }
        >
          {linkText} →
        </Link>
      )}
    </div>
  );
}

function ArticleCard({ article, lang }) {
  const articleTags = Array.isArray(article.tags) ? article.tags : [];

  return (
    <Link
      to={`${ARTICLE_BASE_PATH}/${article.slug}`}
      className={styles.articleCard}
      onClick={() =>
        trackEvent("blog_article_click", {
          event_category: "blog",
          event_label: article.slug,
          article_slug: article.slug,
          destination: `${ARTICLE_BASE_PATH}/${article.slug}`,
          placement: "blog_article_grid",
        })
      }
    >
      <div className={styles.articleImage}>
        <img src={article.image} alt={article.title} />
        <span>{getBlogCategoryLabel(article.category, lang)}</span>
      </div>

      <div className={styles.articleBody}>
        <h3>{article.title}</h3>
        <p>{article.text}</p>

        {articleTags.length > 0 && (
          <div className={styles.articleTags}>
            {articleTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}

        <div className={styles.articleMeta}>
          <span>{article.date}</span>
          <span>◷ {article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}
